import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UserProfileModel } from 'src/app/core/models/user-profile.model';
import { UserProfileService } from '../user-profile.service';
import { FormModalBaseComponent } from 'src/app/core/base/components/form-modal-base/form-modal-base.component';
import { Observable } from 'rxjs';
import { RoleModel } from '../../../../../core/models/role.model';
import { RoleService } from '../../../admin/role/role.service';
import { ContactTypeService } from '../../../settings/contact-type/contact-type.service';
import { ContactTypeModel } from '../../../../../core/models/contact-type.model';
import { BaseContactType } from '../../../../../core/enums/contact-type.enum';
import { AuthService } from '../../../../../core/services/auth.service';
import { ROLE_USER_PROFILE } from '../../../../../core/enums/role_user_profile.enum';
import {
  UserProfileValidatorLevel,
  userProfileValidatorLevelTransalation,
} from '../../../../../core/enums/user-profile-validator-seuil.enum';
import { Typology } from '../../../../../core/enums/typology.enum';
import { ResponseTypeEnum } from '../../../../../core/base/enum/response-type.enum';
import { CrudModeEnum } from '../../../../../core/base/enum/crud-mode.enum';
import { EstablishmentService } from '../../establishment/establishment.service';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { EstablishmentModel } from '../../../../../core/models/establishment.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DefaultSupervisionService } from '../../../../../core/services/default-supervision.service';
import { DefaultSupervisionContactReplacementModalComponent } from '../../../../../core/base/components/default-supervision-tree/default-supervision-contact-replacement-modal/default-supervision-contact-replacement-modal.component';
import { AffectationModel } from '../../../../../core/models/affectation.model';
import { PurchaseActorService } from '../../../../../core/services/purchase-actor.service';
import { PurchaseActorContactReplacementModalComponent } from '../../../../../core/base/components/purchase-actors/purchase-actor-contact-replacement-modal.component';

@Component({
  selector: 'vex-user-profile-create-update',
  templateUrl: './user-profile-create-update-delete.component.html',
  styleUrls: ['./user-profile-create-update-delete.component.scss'],
})
export class UserProfileCreateUpdateDeleteComponent extends FormModalBaseComponent<UserProfileModel> implements OnInit {
  allRoles$: Observable<RoleModel[]>;
  contactType: ContactTypeModel[] = [];
  levels = [];
  temporayRole: Array<string>;
  userFullName: string;
  selectedAffectations: AffectationModel[] = [];
  filteredEstablishments: { [key: number]: EstablishmentModel[] } = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FormModalBaseComponent<UserProfileModel>>,
    private roleService: RoleService,
    public service: UserProfileService,
    private contactTypeService: ContactTypeService,
    public authService: AuthService,
    private establishmentService: EstablishmentService,
    private router: Router,
    public snackbarService: SnackbarService,
    public fb: UntypedFormBuilder,
    private defaultSupervisionService: DefaultSupervisionService,
    private purchaseActorService: PurchaseActorService,
    private dialog: MatDialog,
  ) {
    super(data, dialogRef, service);
  }

  /**
   * Suppression d'un contact référencé comme acteur « Supervision métier » par défaut
   * (PRED-3061) ET/OU comme acteur achat (6 rôles, PRED-3139) : affiche les impacts et propose
   * de remplacer le contact ou de retirer ses affectations, POUR CHAQUE socle, avant de
   * procéder à la suppression. Les deux checks sont chaînés SÉQUENTIELLEMENT (supervision
   * d'abord, puis acteurs achat) — la suppression réelle (`super.deleteItem()`) n'a lieu
   * qu'une fois les DEUX socles vérifiés (et, le cas échéant, traités par leur modale
   * respective). Remplace l'ancien garde-fou (validators/defaultValidators) retiré en Task 15.
   */
  deleteItem(): void {
    this.defaultSupervisionService.checkContactDependencies(this.defaults._id).subscribe({
      next: result => {
        if (!result.data?.hasDependencies) {
          this.checkPurchaseActorDependenciesThenDelete();
          return;
        }
        this.dialog
          .open(DefaultSupervisionContactReplacementModalComponent, {
            width: '650px',
            data: {
              contact: this.defaults,
              dependencies: result.data,
            },
          })
          .afterClosed()
          .subscribe(handled => {
            if (handled) {
              this.checkPurchaseActorDependenciesThenDelete();
            }
          });
      },
      error: () => this.checkPurchaseActorDependenciesThenDelete(),
    });
  }

  /** Second check, chaîné après celui de la supervision (cf. `deleteItem`) : impacts sur les
   * acteurs achat référençant ce contact. Même pattern remplacer/retirer avant de procéder
   * réellement à la suppression du user-profile. */
  private checkPurchaseActorDependenciesThenDelete(): void {
    this.purchaseActorService.checkContactDependencies(this.defaults._id).subscribe({
      next: result => {
        if (!result.data?.hasDependencies) {
          super.deleteItem();
          return;
        }
        this.dialog
          .open(PurchaseActorContactReplacementModalComponent, {
            width: '650px',
            data: {
              contact: this.defaults,
              dependencies: result.data,
            },
          })
          .afterClosed()
          .subscribe(handled => {
            if (handled) {
              super.deleteItem();
            }
          });
      },
      error: () => super.deleteItem(),
    });
  }

  ngOnInit() {
    if (this.isUpdateMode()) {
      this.service.findById(this.defaults._id).subscribe(
        userData => {
          this.userFullName = userData.data.firstName + ' ' + userData.data.lastName;
        },
        error => {
          console.log("Une erreur s'est produite lors de la récupération des données :", error);
        },
      );
    }

    super.ngOnInit();
    // this.allRoles$ = this.roleService.selectItems();
    this.levels = Object.keys(UserProfileValidatorLevel);

    this.temporayRole = Object.values(ROLE_USER_PROFILE);

    this.contactTypeService.findAll().subscribe(x => {
      this.contactType = [...BaseContactType, ...x.data]
        .filter(x => x.typology === Typology.intern)
        .filter((item, index, self) => self.findIndex(t => t._id === item._id) === index);

      if (this.isUpdateMode()) {
        const a = [];
        this.defaults.contactType.forEach(x => {
          const ct = this.contactType.findIndex(y => y._id === x._id);
          if (ct !== -1) {
            a.push(this.contactType[ct]);
          }
        });
        this.form.controls.contactType.setValue(a);
      }
    });

    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      firstName: new UntypedFormControl(this.defaults?.firstName, [Validators.required]),
      lastName: new UntypedFormControl(this.defaults?.lastName, [Validators.required]),
      mcEmail: new UntypedFormControl(this.defaults?.mcEmail, [Validators.required, Validators.email]),
      email: new UntypedFormControl(this.defaults?.email, [Validators.required, Validators.email]),
      // role: new FormControl(this.defaults?.role, [Validators.required]),
      // idDocuware: new FormControl(this.defaults?.idDocuware),
      contactType: new UntypedFormControl(this.defaults?.contactType ?? [], []),
      roles: new UntypedFormControl(this.defaults.roles ?? []),
      // validatorMatriceLevel: new FormControl(this.defaults?.validatorMatriceLevel, [Validators.required]),
      // isPredik: new FormControl(this.defaults?.isPredik ?? false, []),
      phoneNumbers: this.fb.array([]),
      affectAllEstablishments: new UntypedFormControl(
        this.isCreateMode()
          ? false
          : (this.defaults?.affectAllEstablishments || this.defaults?.isPredik) ?? false,
        [],
      ),
    });

    if (this.isUpdateMode() && !this.defaults?.affectAllEstablishments && this.defaults?.affectations?.length) {
      this.selectedAffectations = this.defaults.affectations.map(a => ({
        establishment: { _id: a.establishment?._id, label: a.establishment?.label },
        responsable: { _id: '', fullName: '' },
      }));
    }

    if (this.isCreateMode()) {
      // this.allRoles$.subscribe(role => {
      //     this.form.controls.role.setValue(role.find(x => x.name === 'admin'));
      // });
    }

    if (this.authService.currentUserValue().isPredik) {
      this.form.addControl('isPredik', new UntypedFormControl(this.defaults?.isPredik ?? false));

      // Automatically check affectAllEstablishments checkbox if isPredik is checked
      this.subscription.add(
        this.form.get('isPredik').valueChanges.subscribe((isPredik: boolean) => {
          if (isPredik) {
            this.form.get('affectAllEstablishments').setValue(true);
          }
        }),
      );
    }

    if (this.isUpdateMode()) {
      this.form.controls.mcEmail.disable();
    }
  }

  beforeCreateItem() {
    // this.defaults.contactType = this.defaults?.contactType?.map(x => x.label);
    if (!this.defaults.affectAllEstablishments) {
      this.defaults.affectations = this.selectedAffectations
        .filter(a => a?.establishment?._id)
        .map(a => ({
          establishment: { _id: a.establishment._id, label: a.establishment.label },
          responsable: null,
        }));
    } else {
      this.defaults.affectations = [];
    }
  }

  afterCreateItem(result?: any, error?: any) {
    if (result) {
      this.close(result.data);
    }
  }

  beforeUpdateItem() {
    // this.defaults.contactType = this.defaults?.contactType?.map(x => x.label);
    delete this.defaults.roles;
    if (!this.defaults.affectAllEstablishments) {
      this.defaults.affectations = this.selectedAffectations
        .filter(a => a?.establishment?._id)
        .map(a => ({
          establishment: { _id: a.establishment._id, label: a.establishment.label },
          responsable: null,
        }));
    }
  }

  afterUpdateItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  afterImportItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  levelTranslation(level: string) {
    return userProfileValidatorLevelTransalation(UserProfileValidatorLevel[level]);
  }

  onAffectAllEstablishmentsChange(isChecked: boolean) {
    if (isChecked) {
      this.selectedAffectations = [];
      this.filteredEstablishments = {};
    }
  }

  addAffectation() {
    this.selectedAffectations.push({
      establishment: { _id: '', label: '' },
      responsable: { _id: '', fullName: '' },
    } as AffectationModel);
    this.updateFilteredEstablishments();
  }

  removeAffectation(index: number) {
    this.selectedAffectations.splice(index, 1);
  }

  establishmentAutocompleteChange(state: string, index: number) {
    if (!state) {
      state = '';
    }
    this.establishmentService
      .findAllAsSelect(true, null, 'asc', state, { allEstablishmentsForced: true })
      .pipe(map(x => x.data))
      .subscribe(establishments => {
        const selectedIds = new Set(this.selectedAffectations.map(aff => aff.establishment._id));
        this.filteredEstablishments[index] = establishments.filter(est => !selectedIds.has(est._id));
      });
  }

  updateFilteredEstablishments() {
    const selectedIds = new Set(this.selectedAffectations.map(aff => aff.establishment._id));
    this.establishmentService
      .findAllAsSelect(true, null, 'asc', '', { allEstablishmentsForced: true })
      .pipe(map(x => x.data))
      .subscribe(establishments => {
        this.selectedAffectations.forEach((_, index) => {
          this.filteredEstablishments[index] = establishments.filter(est => !selectedIds.has(est._id));
        });
      });
  }

  onEstablishmentSelect(event: any, index: number) {
    const selectedLabel = event.option.value;
    const selectedId = event.option._element.nativeElement.getAttribute('data-id');

    const isDuplicate = this.selectedAffectations.some(
      (aff, i) => aff.establishment._id === selectedId && i !== index,
    );
    if (isDuplicate) {
      this.snackbarService.danger('Cet établissement est déjà sélectionné.');
      return;
    }

    this.selectedAffectations[index].establishment = { _id: selectedId, label: selectedLabel };
    this.updateFilteredEstablishments();
  }

  async createItem() {
    this.loading = true;
    this.defaults = this.form.getRawValue();
    this.beforeCreateItem();
    if (this.form.valid) {
      const sub = this.service.create(this.defaults).subscribe(
        result => {
          this.afterCreateItem(result, null);
          this.setSnackbar(ResponseTypeEnum.Success, CrudModeEnum.Create, `Création du profil réalisée avec succès`);
        },
        error => {
          this.afterCreateItem(null, error);
          this.setErrorsMessages(error.error.errors);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.onFormInvalid();
      this.logFormErrors(this.form);
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }

  // 2 endpoints différents temporaires car permissions différentes pour l'instant
  // le temps de refacto les permissions
  async updateItem() {
    this.loading = true;
    this.defaults = this.form.getRawValue();
    this.beforeUpdateItem();
    if (this.form.valid) {
      if (this.authService.currentUserValue()._id === this.defaults._id) {
        const sub = this.service.updateFromProfil(this.defaults).subscribe(
          result => {
            this.afterUpdateItem(result, null);
            this.setSnackbar(
              ResponseTypeEnum.Success,
              CrudModeEnum.Update,
              `Modification du profil ${this.userFullName} réalisée avec succès`,
            );
            location.reload();
          },
          error => {
            this.afterUpdateItem(null, error);
            this.setErrorsMessages(error.error.errors);
            this.loading = false;
          },
        );
        this.subscription.add(sub);
      } else {
        const sub = this.service.update(this.defaults).subscribe(
          result => {
            this.afterUpdateItem(result, null);
            this.setSnackbar(
              ResponseTypeEnum.Success,
              CrudModeEnum.Update,
              `Modification du profil ${this.userFullName} réalisée avec succès`,
            );
            location.reload();
          },
          error => {
            this.afterUpdateItem(null, error);
            this.setErrorsMessages(error.error.errors);
            this.loading = false;
          },
        );
        this.subscription.add(sub);
      }
    } else {
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }
}
